import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import Topbar from "@/components/layout/Topbar";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, PencilSimple, TrashSimple, MagnifyingGlass, Buildings, Gear } from "@phosphor-icons/react";

const emptyForm = () => ({ nombre: "", tipo: "", cuit: "", observaciones: "" });

const fmtARS = (n) => (Number(n) || 0).toLocaleString("es-AR", { style: "currency", currency: "ARS" });

export default function PasivosEntidadesPage() {
  const [entidades, setEntidades] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [delTarget, setDelTarget] = useState(null);

  // Gestión de tipos
  const [tiposOpen, setTiposOpen] = useState(false);
  const [newTipo, setNewTipo] = useState("");
  const [delTipo, setDelTipo] = useState(null);

  const load = async () => {
    try {
      const [{ data: ents }, { data: ts }] = await Promise.all([
        api.get("/pasivos/entidades"),
        api.get("/pasivos/tipos"),
      ]);
      setEntidades(ents || []);
      setTipos(ts || []);
    } catch (e) {
      toast.error("No se pudieron cargar los datos");
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return entidades;
    return entidades.filter((e) =>
      String(e.nombre || "").toLowerCase().includes(s) ||
      String(e.cuit || "").includes(s) ||
      String(e.tipo || "").toLowerCase().includes(s)
    );
  }, [entidades, q]);

  const openNuevo = () => {
    setEditingId(null);
    setForm({ ...emptyForm(), tipo: tipos[0] || "" });
    setOpen(true);
  };

  const openEdit = (e) => {
    setEditingId(e.id);
    setForm({
      nombre: e.nombre || "",
      tipo: e.tipo || "Otro",
      cuit: e.cuit || "",
      observaciones: e.observaciones || "",
    });
    setOpen(true);
  };

  const guardar = async () => {
    if (!form.nombre.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/pasivos/entidades/${editingId}`, form);
        toast.success("Entidad actualizada");
      } else {
        await api.post("/pasivos/entidades", form);
        toast.success("Entidad creada");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const eliminar = async () => {
    if (!delTarget) return;
    try {
      await api.delete(`/pasivos/entidades/${delTarget.id}`);
      toast.success("Entidad eliminada");
      setDelTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const crearTipo = async () => {
    const nombre = newTipo.trim();
    if (!nombre) return toast.error("Nombre del tipo requerido");
    try {
      await api.post("/pasivos/tipos", { nombre });
      toast.success("Tipo creado");
      setNewTipo("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al crear tipo");
    }
  };

  const eliminarTipo = async () => {
    if (!delTipo) return;
    try {
      await api.delete(`/pasivos/tipos/${encodeURIComponent(delTipo)}`);
      toast.success("Tipo eliminado");
      setDelTipo(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  return (
    <>
      <Topbar
        title="Pasivo Fin./Fiscal · Entidades"
        subtitle="Alta y edición de entidades (AFIP, ARBA, bancos, financieras, etc.)"
      />
      <div className="p-4 space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[260px]">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por nombre, CUIT o tipo…"
              className="pl-8 rounded-sm"
              data-testid="pasivos-entidades-search"
            />
          </div>
          <Button
            onClick={openNuevo}
            className="rounded-sm bg-[#A02836] hover:bg-[#821E2A]"
            data-testid="pasivos-entidades-nueva"
          >
            <Plus size={14} className="mr-1" /> Nueva entidad
          </Button>
          <Button
            variant="outline"
            onClick={() => setTiposOpen(true)}
            className="rounded-sm"
            data-testid="pasivos-tipos-manage"
          >
            <Gear size={14} className="mr-1" /> Tipos de entidad
          </Button>
        </div>

        <div className="border border-[#E5E5E5] bg-white rounded-sm overflow-hidden">
          <table className="w-full text-[13px]">
            <thead className="bg-neutral-50 text-[11px] uppercase tracking-widest text-neutral-500 font-mono">
              <tr>
                <th className="text-left px-3 py-2">Nombre</th>
                <th className="text-left px-3 py-2 w-[100px]">Tipo</th>
                <th className="text-left px-3 py-2 w-[140px]">CUIT</th>
                <th className="text-right px-3 py-2 w-[80px]">Deudas</th>
                <th className="text-right px-3 py-2 w-[180px]">Saldo pendiente</th>
                <th className="text-right px-3 py-2 w-[120px]">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={6} className="text-center text-neutral-400 py-6">Sin entidades cargadas.</td></tr>
              ) : filtered.map((e) => (
                <tr key={e.id} className="border-t border-neutral-100 hover:bg-neutral-50/50" data-testid={`entidad-row-${e.id}`}>
                  <td className="px-3 py-2 font-medium">{e.nombre}</td>
                  <td className="px-3 py-2">
                    <span className="text-[11px] px-2 py-0.5 rounded-sm bg-neutral-100 uppercase tracking-wider font-mono">
                      {e.tipo || "Otro"}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono text-[12px] text-neutral-600">{e.cuit || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono">{e.total_deudas || 0}</td>
                  <td className="px-3 py-2 text-right font-mono font-semibold">
                    {e.saldo_total > 0 ? (
                      <span className="text-[#A02836]">{fmtARS(e.saldo_total)}</span>
                    ) : (
                      <span className="text-neutral-400">$ 0,00</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => openEdit(e)}
                      className="text-neutral-500 hover:text-[#A02836] p-1"
                      data-testid={`entidad-edit-${e.id}`}
                      title="Editar"
                    >
                      <PencilSimple size={16} />
                    </button>
                    <button
                      onClick={() => setDelTarget(e)}
                      className="text-neutral-500 hover:text-red-600 p-1"
                      data-testid={`entidad-del-${e.id}`}
                      title="Eliminar"
                    >
                      <TrashSimple size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Dialog nueva / editar */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Buildings size={18} />
              {editingId ? "Editar entidad" : "Nueva entidad"}
            </DialogTitle>
            <DialogDescription>
              Datos generales de la entidad acreedora.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[12px]">Nombre *</Label>
              <Input
                value={form.nombre}
                onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))}
                placeholder="Ej: PLAN DE PAGO AFIP 1854"
                data-testid="entidad-form-nombre"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[12px]">Tipo</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm((f) => ({ ...f, tipo: v }))}>
                  <SelectTrigger data-testid="entidad-form-tipo"><SelectValue placeholder="Seleccionar…" /></SelectTrigger>
                  <SelectContent>
                    {tipos.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[12px]">CUIT</Label>
                <Input
                  value={form.cuit}
                  onChange={(e) => setForm((f) => ({ ...f, cuit: e.target.value }))}
                  placeholder="20-12345678-9"
                  data-testid="entidad-form-cuit"
                />
              </div>
            </div>
            <div>
              <Label className="text-[12px]">Observaciones</Label>
              <Textarea
                value={form.observaciones}
                onChange={(e) => setForm((f) => ({ ...f, observaciones: e.target.value }))}
                rows={3}
                data-testid="entidad-form-obs"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button
              onClick={guardar}
              disabled={saving}
              className="bg-[#A02836] hover:bg-[#821E2A]"
              data-testid="entidad-form-save"
            >
              {saving ? "Guardando…" : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog gestión de tipos */}
      <Dialog open={tiposOpen} onOpenChange={setTiposOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Gear size={18} /> Tipos de entidad
            </DialogTitle>
            <DialogDescription>
              Agregá o eliminá los tipos disponibles para clasificar las entidades. No podés eliminar un tipo que esté en uso.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Label className="text-[12px]">Nuevo tipo</Label>
                <Input
                  value={newTipo}
                  onChange={(e) => setNewTipo(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); crearTipo(); } }}
                  placeholder="Ej: Municipal"
                  data-testid="tipo-nuevo-input"
                />
              </div>
              <Button onClick={crearTipo} className="bg-[#A02836] hover:bg-[#821E2A]" data-testid="tipo-nuevo-save">
                <Plus size={14} className="mr-1" /> Agregar
              </Button>
            </div>
            <div className="border border-neutral-200 rounded-sm max-h-[280px] overflow-y-auto">
              {tipos.length === 0 ? (
                <div className="p-4 text-center text-[12px] text-neutral-400">Sin tipos configurados.</div>
              ) : tipos.map((t) => {
                const enUso = entidades.filter((e) => e.tipo === t).length;
                return (
                  <div key={t} className="flex items-center justify-between px-3 py-2 border-b last:border-b-0 border-neutral-100" data-testid={`tipo-row-${t}`}>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] px-2 py-0.5 rounded-sm bg-neutral-100 uppercase tracking-wider font-mono">{t}</span>
                      {enUso > 0 && (
                        <span className="text-[11px] text-neutral-500">{enUso} entidad{enUso !== 1 ? "es" : ""}</span>
                      )}
                    </div>
                    <button
                      onClick={() => setDelTipo(t)}
                      disabled={enUso > 0}
                      className="text-neutral-400 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed p-1"
                      title={enUso > 0 ? "No se puede eliminar (en uso)" : "Eliminar tipo"}
                      data-testid={`tipo-del-${t}`}
                    >
                      <TrashSimple size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTiposOpen(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmación eliminación tipo */}
      <AlertDialog open={!!delTipo} onOpenChange={(v) => !v && setDelTipo(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Eliminar tipo "{delTipo}"</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Seguro? Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={eliminarTipo} className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Alert eliminación */}
      <AlertDialog open={!!delTarget} onOpenChange={(v) => !v && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Eliminar entidad</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Seguro que querés eliminar "{delTarget?.nombre}"? Sólo se puede eliminar si no tiene deudas cargadas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={eliminar} className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
